import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../../lib/api';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const Card = ({ children, testid }) => <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>{children}</div>;
const Sel = (props) => <select {...props} className={`border border-slate-200 rounded-md px-2 py-2 text-sm bg-white ${props.className || ''}`} />;

const GradeEntry = () => {
  const qc = useQueryClient();
  const { data: sections } = useQuery({ queryKey: ['acad-sections'], queryFn: () => api.get('/acad/sections').then((r) => r.data) });
  const [sectionId, setSectionId] = useState('');
  const { data: roster } = useQuery({ enabled: !!sectionId, queryKey: ['acad-roster', sectionId], queryFn: () => api.get('/acad/registrations', { params: { section_id: sectionId } }).then((r) => r.data) });
  const { data: grades } = useQuery({ enabled: !!sectionId, queryKey: ['acad-grades', sectionId], queryFn: () => api.get('/acad/grades', { params: { section_id: sectionId } }).then((r) => r.data) });
  const [vals, setVals] = useState({});
  const [override, setOverride] = useState(false);
  const gradeOf = (sid) => (grades || []).find((g) => g.student_id === sid);
  const registered = (roster || []).filter((r) => r.status === 'registered');
  const save = async () => {
    const entries = Object.entries(vals).filter(([, v]) => String(v).trim() !== '').map(([student_id, value]) => ({ student_id, value: String(value) }));
    if (!entries.length) return toast.error('Enter at least one grade');
    try {
      const { data } = await api.post('/acad/grades', { section_id: sectionId, entries, override, reason: override ? 'Entered outside the grading window' : null });
      toast.success(`${data.saved} grade(s) saved - awaiting approval`);
      setVals({}); qc.invalidateQueries({ queryKey: ['acad-grades', sectionId] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="grade-entry">
      <p className="font-display font-bold mb-1">Enter grades</p>
      <p className="text-xs text-slate-500 mb-2">Grades are checked against the course's grading scheme and only count once an approver makes them official.</p>
      <div className="flex gap-2 flex-wrap mb-3 items-center">
        <Sel data-testid="grade-section" value={sectionId} onChange={(e) => { setSectionId(e.target.value); setVals({}); }}>
          <option value="">Section...</option>{(sections || []).map((s) => <option key={s.id} value={s.id}>{s.course_code} {s.section_code}</option>)}
        </Sel>
        <label className="text-xs text-slate-600 flex items-center gap-1"><input data-testid="grade-override" type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Override grading window (approvers only)</label>
      </div>
      {sectionId && registered.map((r) => {
        const g = gradeOf(r.student_id);
        return (
          <div key={r.id} className="border-b border-slate-50 py-1.5 flex items-center gap-3 text-sm" data-testid={`grade-row-${r.student_id}`}>
            <span className="w-48 truncate">{r.student_name}</span>
            {g && g.status === 'official' ? (
              <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">Official: {String(g.value)}</span>
            ) : (
              <>
                <Input data-testid={`grade-input-${r.student_id}`} className="w-28" placeholder={g ? String(g.value) : 'Grade'} value={vals[r.student_id] || ''} onChange={(e) => setVals({ ...vals, [r.student_id]: e.target.value })} />
                {g && <span className={`text-[10px] px-1.5 py-0.5 rounded ${g.status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{g.status === 'rejected' ? 'sent back' : 'awaiting approval'}</span>}
              </>
            )}
          </div>
        );
      })}
      {sectionId && registered.length === 0 && <p className="text-sm text-slate-400">No registered students on this section.</p>}
      {sectionId && registered.length > 0 && <Button data-testid="grade-save" className="mt-3 bg-slate-900 hover:bg-slate-800" onClick={save}>Save grades for approval</Button>}
    </Card>
  );
};

const Approvals = () => {
  const qc = useQueryClient();
  const { data: pending, isError } = useQuery({ queryKey: ['acad-pending-grades'], retry: false, queryFn: () => api.get('/acad/grades/pending').then((r) => r.data) });
  const [sel, setSel] = useState({});
  if (isError) return null;
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const decide = async (approve) => {
    try {
      const { data } = await api.post('/acad/grades/decide', { grade_ids: ids, approve, reason: approve ? 'Approved as official' : 'Sent back for re-entry' });
      toast.success(`${data.decided} grade(s) ${approve ? 'made official' : 'sent back'}`);
      setSel({}); qc.invalidateQueries({ queryKey: ['acad-pending-grades'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="grade-approvals">
      <p className="font-display font-bold mb-2">Awaiting approval</p>
      {(pending || []).map((g) => (
        <label key={g.id} className="border-b border-slate-50 py-1.5 flex items-center gap-2 text-sm cursor-pointer" data-testid={`approve-row-${g.id}`}>
          <input type="checkbox" data-testid={`approve-check-${g.id}`} checked={!!sel[g.id]} onChange={(e) => setSel({ ...sel, [g.id]: e.target.checked })} />
          <span>{g.student_name} · <b>{g.course_code} {g.section_code}</b> · {String(g.value)} <span className="text-xs text-slate-400">entered by {g.entered_by}</span></span>
        </label>
      ))}
      {(pending || []).length === 0 && <p className="text-sm text-slate-400">Nothing waiting - entered grades appear here for a second pair of eyes.</p>}
      {ids.length > 0 && (
        <div className="flex gap-2 mt-3">
          <Button data-testid="approve-grades" size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => decide(true)}>Make official ({ids.length})</Button>
          <Button data-testid="reject-grades" size="sm" variant="outline" onClick={() => decide(false)}>Send back</Button>
        </div>
      )}
    </Card>
  );
};

const Transcripts = () => {
  const { data: students } = useQuery({ queryKey: ['students-all'], queryFn: () => api.get('/students').then((r) => r.data).catch(() => []) });
  const [sid, setSid] = useState('');
  const { data: tr } = useQuery({ enabled: !!sid, queryKey: ['acad-transcript', sid], queryFn: () => api.get(`/acad/transcript/${sid}`).then((r) => r.data) });
  return (
    <Card testid="transcripts-panel">
      <p className="font-display font-bold mb-2">Transcripts</p>
      <div className="flex gap-2 flex-wrap items-center">
        <Sel data-testid="transcript-student" value={sid} onChange={(e) => setSid(e.target.value)}>
          <option value="">Student...</option>{(students || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Sel>
        {sid && (
          <>
            <a data-testid="transcript-pdf" href={`${process.env.REACT_APP_BACKEND_URL}/api/acad/transcript/${sid}/pdf`} target="_blank" rel="noreferrer"
              className="text-sm bg-slate-900 text-white rounded-md px-3 py-2 hover:bg-slate-800">Download official transcript (PDF)</a>
            <a data-testid="transcript-pdf-unofficial" href={`${process.env.REACT_APP_BACKEND_URL}/api/acad/transcript/${sid}/pdf?official=false`} target="_blank" rel="noreferrer"
              className="text-sm border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50">Unofficial copy (PDF)</a>
          </>
        )}
      </div>
      {tr && (
        <div className="mt-3 text-sm" data-testid="transcript-summary">
          <p className="text-xs text-slate-500 mb-1">{tr.programmes.map((p) => `${p.code} ${p.name} (${p.status})`).join(' · ') || 'No programme on record'}</p>
          {tr.terms.map((b) => (
            <div key={b.term} className="border-b border-slate-50 py-1.5">
              <b>{b.term}</b> <span className="text-xs text-slate-400">{b.credits} credits · GPA {b.gpa ?? '-'}</span>
              {b.rows.map((g) => <p key={g.id} className="text-xs text-slate-600">{g.course_code} {g.course_title} · {String(g.value)} · {g.credit_value} cr</p>)}
            </div>
          ))}
          <p className="mt-2"><b>Cumulative:</b> {tr.credits_earned} credits · GPA {tr.cumulative_gpa ?? '-'}</p>
          {tr.terms.length === 0 && <p className="text-slate-400">No official grades yet.</p>}
        </div>
      )}
    </Card>
  );
};

export const GradingTab = () => (
  <div className="space-y-4">
    <GradeEntry />
    <Approvals />
    <Transcripts />
  </div>
);
